<script lang="ts">
  /**
   * The marketing site's hero, reduced to a backdrop for the auth surfaces.
   *
   * Purely decorative: the whole subtree is `aria-hidden`, holds no text, no
   * control, and no focusable node, and is `pointer-events: none`, so it adds
   * nothing to the accessibility tree or the tab order. Everything it draws is
   * either a brand token or the site's own wave geometry, inlined as SVG rather
   * than shipped as an asset so it inherits the palette and costs no request.
   *
   * The site stacks three wave layers at 0.7 / 0.8 / 0.9 across a full-bleed hero.
   * Behind a 420px auth card that reads as noise, so this is two layers at 0.35 and
   * 0.5 — enough to place the brand, quiet enough that the card stays the subject.
   *
   * Under `prefers-reduced-motion` the layers are not merely frozen (a stopped wave
   * is a half-drawn artefact): they are removed and the container falls back to
   * --hero-static, the flat gradient standing in for the animated field.
   */

  // Gradient ids must be unique per instance: two backdrops on one document would
  // otherwise share the first instance's <defs>, and `url(#…)` resolves document-wide.
  const uid = $props.id();
  const backGradient = `${uid}-wave-back`;
  const frontGradient = `${uid}-wave-front`;
</script>

<div class="hero-bg" aria-hidden="true">
  <div class="hero-glow"></div>

  <!--
    Each layer is a 200%-wide tile pair: the second copy is the first translated by
    one tile width, so waveSlide's -50% endpoint lands pixel-identical to its start
    and the loop has no seam.
  -->
  <div class="wave wave-back">
    <svg viewBox="0 0 2880 360" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={backGradient} x1="0" y1="0" x2="0" y2="1">
          <stop class="stop-top" offset="0%" />
          <stop class="stop-bottom" offset="100%" />
        </linearGradient>
      </defs>
      <g fill="url(#{backGradient})">
        <path d="M0,170 C240,110 480,230 720,170 C960,110 1200,230 1440,170 L1440,360 L0,360 Z" />
        <path
          d="M0,170 C240,110 480,230 720,170 C960,110 1200,230 1440,170 L1440,360 L0,360 Z"
          transform="translate(1440 0)"
        />
      </g>
    </svg>
  </div>

  <div class="wave wave-front">
    <svg viewBox="0 0 2880 360" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={frontGradient} x1="0" y1="0" x2="0" y2="1">
          <stop class="stop-top" offset="0%" />
          <stop class="stop-bottom" offset="100%" />
        </linearGradient>
      </defs>
      <g fill="url(#{frontGradient})">
        <path
          d="M0,260 C120,210 240,310 360,260 C480,210 600,310 720,260 C840,210 960,310 1080,260 C1200,210 1320,310 1440,260 L1440,360 L0,360 Z"
        />
        <path
          d="M0,260 C120,210 240,310 360,260 C480,210 600,310 720,260 C840,210 960,310 1080,260 C1200,210 1320,310 1440,260 L1440,360 L0,360 Z"
          transform="translate(1440 0)"
        />
      </g>
    </svg>
  </div>
</div>

<style>
  .hero-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    /* Also the reduced-motion fallback: with the layers gone this is all that shows. */
    background: var(--hero-static);
    pointer-events: none;
  }
  .hero-glow {
    position: absolute;
    left: 50%;
    top: -180px;
    width: 760px;
    height: 360px;
    transform: translateX(-50%);
    background: radial-gradient(closest-side, var(--hero-glow-core), transparent 70%);
    filter: blur(8px);
    animation: glowPulse 9s ease-in-out infinite;
  }
  /* Anchored to the bottom edge, so the field reads as a horizon under the card
     rather than crowding its heading. */
  .wave {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 200%;
  }
  .wave svg {
    display: block;
    width: 100%;
    height: 100%;
  }
  .wave-back {
    height: 62%;
    opacity: 0.35;
    animation: waveSlide 38s linear infinite reverse;
  }
  .wave-front {
    height: 44%;
    opacity: 0.5;
    animation: waveSlide 22s linear infinite;
  }
  /* Vertical fade so each layer emerges from the page instead of ending on a hard
     edge. Set here rather than as presentation attributes so the stops stay on the
     accent ladder. */
  .wave-back .stop-top {
    stop-color: var(--accent-dim);
    stop-opacity: 0;
  }
  .wave-back .stop-bottom {
    stop-color: var(--accent-dim);
    stop-opacity: 0.55;
  }
  .wave-front .stop-top {
    stop-color: var(--accent-bright);
    stop-opacity: 0;
  }
  .wave-front .stop-bottom {
    stop-color: var(--accent-bright);
    stop-opacity: 0.4;
  }

  /*
   * will-change is a standing promise to the compositor — it pins a layer for the
   * lifetime of the element. Make it only where something is actually animating, so
   * the reduced-motion path (and any paused tab) does not hold three idle layers.
   */
  @media (prefers-reduced-motion: no-preference) {
    .wave {
      will-change: transform;
    }
    .hero-glow {
      will-change: opacity, transform;
    }
  }

  /* motion.css carries the same collapse globally; repeated here so the component is
     correct on its own and the fallback is not a frozen half-wave. */
  @media (prefers-reduced-motion: reduce) {
    .hero-glow,
    .wave {
      display: none;
      animation: none;
      will-change: auto;
    }
    .hero-bg {
      background: var(--hero-static);
    }
  }
</style>
